import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { switchMap, tap } from 'rxjs/operators';
import { AddressComponent } from '../../../common/component/address/address.component';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { MeterMediaType } from '../../../consumption/model/const/enums';
import { Meter } from '../../../consumption/model/meter';
import { RentalObjectConsumptionMeter } from '../../../consumption/model/rentalobject-consumption-meter';
import { MeterService } from '../../../consumption/service/meter.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { standardCommand, Command } from '../../../core/model/command';
import { Money } from '../../../core/model/money';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { DwellingInspection } from '../../../dwelling-inspection/model/dwelling-inspection';
import { FaultNotificationService } from '../../../fault-notification/service/data/fault-notification.service';
import { LettingAdvertisment } from '../../../letting/model/letting-advertisment';
import { LettingAdvertismentService } from '../../../letting/service/letting-advertisment.service';
import { rentalObjectCommand } from '../../command/rental-object.command';
import { Building } from '../../model/building';
import { rentalObjectFormCommand } from '../../model/const/command';
import {
  CustomPropertyDataType,
  FormOfLease,
  FormOfLeaseToString,
  LeaseAgreementCategory,
  RentalObjectCategory,
} from '../../model/const/enums';
import { Contract } from '../../model/contract';
import { Dwelling } from '../../model/dwelling';
import { Entrance } from '../../model/entrance';
import { LinkedCustomProperty } from '../../model/linked-custom-property';
import { ObjectComponent } from '../../model/object-component';
import { PlannedMaintenanceAction } from '../../model/object-component-planned-maintenance';
import { ObjectComponentPropertyValue } from '../../model/object-component-property-value';
import { RentApplication } from '../../model/rent-application';
import { RentalObjectKey } from '../../model/rental-object-key';
import { Room } from '../../model/room';
import { CreateContractRentalObjectCommandHandler } from '../../service/command/create-contract-rental-object-command-handler.service';
import { RentalObjectFormCommandHandlerResolver } from '../../service/command/rental-object-form-command-handler-resolver.service';
import { BuildingService } from '../../service/data/building.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { CompanyService } from '../../service/data/company.service';
import { DwellingService } from '../../service/data/dwelling.service';
import { EntranceService } from '../../service/data/entrance.service';
import { PropertyService } from '../../service/data/property.service';
import { RentalObjectLogService } from '../../service/data/rental-object-log.service';
import { RentalObjectTypeService } from '../../service/data/rental-object-type.service';
import { RoomService } from '../../service/data/room.service';
import { RentalObjectFormComponent } from '../base/rental-object.base';
import { LocalDate } from '../../../core/model/local-date';
import { LicenseService } from '../../../common/service/data/license.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';

const formControlName = {
  apartmentNumber: 'apartmentNumber',
  building: 'building',
  buildingFloors: 'buildingFloors',
  elevator: 'elevator',
  floor: 'floor',
  indvidualmeterelectricity: 'indvidualmeterelectricity',
  indvidualmeterwater: 'indvidualmeterwater',
  indvidualmetercoldwater: 'indvidualmetercoldwater',
  indvidualmeterheating: 'indvidualmeterheating',
  numberOfRooms: 'numberOfRooms',
  rentalObjectType: 'rentalObjectType',
  entrance: 'entrance',
  furnished: 'furnished',
  atticStorageNumber: 'atticStorageNumber',
  basementStorageNumber: 'basementStorageNumber',
  otherStorageNumber: 'otherStorageNumber',
  parkingIncludedInRent: 'parkingIncludedInRent'
};

@Component({
    selector: 'app-dwelling-form',
    templateUrl: './dwelling-form.component.html',
    styleUrls: ['./dwelling-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        CreateContractRentalObjectCommandHandler,
        RentalObjectFormCommandHandlerResolver,
        LettingAdvertismentService,
        { provide: 'category', useValue: RentalObjectCategory.Dwelling },
        { provide: DataService, useExisting: DwellingService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DwellingFormComponent extends RentalObjectFormComponent<Dwelling> {
  protected override get category(): RentalObjectCategory {
    return RentalObjectCategory.Dwelling;
  }

  constructor(
    private _buildingService: BuildingService,
    private _entranceService: EntranceService,
    private _cdf: ColumnDefinitionFactory,
    private _fcf: FormControlExFactory,
    private _accountService: UserAccountService,
    private _meterService: MeterService,
    private _roomService: RoomService,
    private _advertService: LettingAdvertismentService,
    faultNotificationService: FaultNotificationService,
    activatedRoute: ActivatedRoute,
    companyService: CompanyService,
    commandDispatcher: CommandDispatcher,
    private _dataService: DwellingService,
    cds: ColumnDefinitionService,
    fb: FormBuilder,
    handlerResolver: RentalObjectFormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    propertyService: PropertyService,
    rentalObjectTypeService: RentalObjectTypeService,
    columnDefinitionService: ColumnDefinitionService,
    logService: RentalObjectLogService,
    licenseService: LicenseService,
    dimensionLabelsService: DimensionLabelsService
  ) {
    super(
      fb,
      _fcf,
      companyService,
      propertyService,
      rentalObjectTypeService,
      faultNotificationService,
      cds,
      activatedRoute,
      commandDispatcher,
      handlerResolver,
      _dataService,
      messageService,
      navigation,
      columnDefinitionService,
      logService,
      licenseService,
      dimensionLabelsService
    );

    this.AddSubscription(
      this._accountService.current.subscribe({
        next: account => {
          const profileSettings = this._accountService.profileSettings;

          if (profileSettings.useBrf) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.BROKER_INFORMATION', rentalObjectCommand.printBrokerInformation);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('VACANT_RENTAL_OBJECT_LIST.CREATE_LEASE_AGREEMENT_BTN', rentalObjectFormCommand.createContract);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', rentalObjectCommand.showInvoices);
          if (
            account.hasAccess(ModuleNames.Letting, Features.Letting_HomeQ) ||
            account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)
          ) {
            this.addMenuItemDefinition('RENTAL_OBJECT.COMMAND.ADVERTISE', rentalObjectCommand.advertise);
            this.showAdverts = true;
          }
          if (
            account.hasAccess(ModuleNames.Properties, Features.LinkPropertyBaseDocuments) ||
            account.hasAccess(ModuleNames.Properties, Features.EditDwelling)
          ) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', rentalObjectCommand.linkDocument);
          }
          if (account.hasAccess(ModuleNames.Properties, Features.EditDwelling)) {
            this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
            this.addMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
          if (account.hasAccess(ModuleNames.Properties, Features.ChangeRentalObjectCategory)) {
            const addChangeCategory = () => {
              if (this.dataContext?.id) this.addMenuItemDefinition('RENTAL_OBJECT_CATEGORY.COMMAND', rentalObjectCommand.changeCategory);
            };
            addChangeCategory();
            this.AddSubscription(this.dataContextInitialized$.subscribe(addChangeCategory));
          }
        },
      })
    );

    this.leaseAgreementColumnDefinition = [
      this._cdf.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption'),
      this._cdf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._cdf.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      this._cdf.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      this._cdf.createDate('LEASE_AGREEMENT.AGREEMENT_DATE', 'agreementDate', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE_NAME', 'tenantOne'),
      this._cdf.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount'),
      this._cdf.createMoney('COMMON.YEAR_RENT', 'totalYearRent'),
      this._cdf.createMoney('COMMON.YEAR_RENT_PER_SQM', 'totalYearRentPerSqm', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
    ];

    this.dwellingInspectionColumnDefinition = [
      this._cdf.createTranslation('COMMON.STATUS', 'statusCaption'),
      this._cdf.create('DWELLING_INSPECTION.NUMBER', 'number'),
      this._cdf.createDate('DWELLING_INSPECTION.INSPECTION_DATE', 'inspectionDate'),
      this._cdf.create('DWELLING_INSPECTION.INSPECTOR', 'inspector'),
      this._cdf.create('DWELLING_INSPECTION.INSPECTION_TYPE', 'dwellingInspectionType'),
    ];

    this.vacantPeriodsColumnDefinition = [
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_FROM', 'fromDate'),
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_TO', 'toDate'),
      this._cdf.createMoney('COMMON.RENT_LOSS_THIS_YEAR', 'rentLossCurrentYear'),
    ];

    this.rentApplicationsColumnDefinition = [
      this._cdf.createDate('RENT_APPLICATION.REGISTRATION_DATE', 'applicationDate', ColumnVisibility.default),
      this._cdf.create('TENANT.CONTACT_NUMBER', 'contactCustomerNumber', ColumnVisibility.optional),
      this._cdf.create('TENANT.NAME', 'contactName', ColumnVisibility.mandatory),
      this._cdf.create('TENANT.CIVIC_ORG_NUMBER', 'contactIdentification', ColumnVisibility.optional),
      this._cdf.create('TENANT.EMAIL', 'contactEmail', ColumnVisibility.default),
      this._cdf.create('TENANT.PHONE', 'contactPhone', ColumnVisibility.default),
      this._cdf.create('RENT_APPLICATION.MIN_ROOMS', 'numberOfRoomsMin', ColumnVisibility.default),
      this._cdf.create('RENT_APPLICATION.MAX_ROOMS', 'numberOfRoomsMax', ColumnVisibility.optional),
      this._cdf.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.default),
    ];

    this.advertismentsColumnDefinition = [
      this._cdf.createDate('LETTING.ADVERTISMENT.CREATED_AT', 'createdAt', ColumnVisibility.mandatory),
      this._cdf.createTranslation('LETTING.ADVERTISMENT.PLATFORM', 'platformName', ColumnVisibility.mandatory),
      this._cdf.createDate('LETTING.ADVERTISMENT.PUBLISH_FROM', 'publishFrom', ColumnVisibility.default),
      this._cdf.createDate('LETTING.ADVERTISMENT.PUBLISH_TO', 'publishTo', ColumnVisibility.default),
      this._cdf.createDate('LETTING.ADVERTISMENT.VACANT_FROM', 'vacancyFrom', ColumnVisibility.default),
      this._cdf.createDate('LETTING.ADVERTISMENT.VACANT_TO', 'vacancyTo', ColumnVisibility.optional),
      this._cdf.createTranslation('LETTING.ADVERTISMENT.STATE_COLUMN', 'platformState', ColumnVisibility.default),
    ];
  }

  leaseAgreementColumnDefinition: ColumnDefinition[];
  dwellingInspectionColumnDefinition: ColumnDefinition[];
  vacantPeriodsColumnDefinition: ColumnDefinition[];
  rentApplicationsColumnDefinition: ColumnDefinition[];
  advertismentsColumnDefinition: ColumnDefinition[];
  showAdverts: boolean = false;
  advertisments: LettingAdvertisment[];
  accordionHeader: string = 'LEASE_AGREEMENT_ROW.LIST_HEADER';

  get leaseAgreementRowHeader(): string {
    if (this.showBrf) return 'LEASE_AGREEMENT_ROW.LIST_HEADER_BRF';
    return 'LEASE_AGREEMENT_ROW.LIST_HEADER';
  }

  get leaseAgreementRowHelpText(): string {
    if (this.showBrf) return 'LEASE_AGREEMENT_ROW.HELP_TEXT.RENTAL_OBJECT_RENT_BRF';

    return 'LEASE_AGREEMENT_ROW.HELP_TEXT.RENTAL_OBJECT_RENT';
  }

  @ViewChild(AddressComponent) addressView: AddressComponent;

  private _buildings: Building[];
  buildings: any[];

  private _entrances: Entrance[];
  entrances: any[];

  private _filterBuildingsByProperty(property: string) {
    if (!this._buildings) return;

    this.buildings = this._buildings
      .filter(b => b.property === property)
      .map(b => {
        return { name: b.toString(), value: b.id };
      });

    if (this.buildings.length == 1 && this.isNew) {
      let buildingId = this.buildings[0].value;
      this.form.controls[formControlName.building].setValue(buildingId);
      this.form.controls[formControlName.entrance].setValue(this._buildings.find(b => b.id === buildingId).entrances[0].id);
    }
  }

  private _filterEntranceByBuilding(building: string) {
    if (!this._entrances) return;

    this.entrances = this._entrances
      .filter(b => b.building === building)
      .map(b => {
        return { name: b.name, value: b.id };
      });

    if (this.entrances.length == 1 && this.isNew) {
      let entranceId = this.entrances[0].value;
      this.form.controls[formControlName.entrance].setValue(entranceId);
    }
  }

  private _setBuildingFloors(buildingId: string) {
    let control = this.form.controls[formControlName.buildingFloors];
    if (!control) return;

    let building = this._buildings?.find(b => b.id === buildingId);
    control.setValue(building ? building.floorsInBuilding : null);
  }

  protected createModel(): Dwelling {
    return new Dwelling();
  }

  brfForm: FormGroup;

  get showBrf(): boolean {
    let formOfLease = this.form.controls['formOfLease'].value;
    return formOfLease === FormOfLease.CooperativeHousing || formOfLease === FormOfLease.Ownership;
  }

  override formSubmittedChange(response?: any) {
    this.changedAt = response;
    super.formSubmittedChange(response);
  }

  protected getFormGroup(): FormGroup {
    let group = super.getFormGroup();
    this.brfForm = this.fb.group({
      share: this._fcf.create('COOPERATIVE_HOUSING.SHARE', this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.share : null),
      sharePercentage: this._fcf.createReadonly(
        'COOPERATIVE_HOUSING.SHARE_PERCENT',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.sharePercentage : null
      ),
      shareCompanySum: this._fcf.createReadonly(
        'COOPERATIVE_HOUSING.SHARE_COMPANY_SUM',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.shareCompanySum : null
      ),
      contributionAmount: this._fcf.create(
        'COOPERATIVE_HOUSING.CONTRIBUTION_AMOUNT',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.contributionAmount : new Money(this.dataContext.currencyCode, null)
      ),
      baseAnnualFee: this._fcf.create(
        'COOPERATIVE_HOUSING.BASE_ANNUAL_FEE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.baseAnnualFee : new Money(this.dataContext.currencyCode, null)
      ),
      assignmentFee: this._fcf.create(
        'COOPERATIVE_HOUSING.ASSIGNMENT_FEE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.assignmentFee : new Money(this.dataContext.currencyCode, null)
      ),
      assignmentDate: this._fcf.create(
        'COOPERATIVE_HOUSING.ASSIGNMENT_DATE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.assignmentDate : null
      ),
      economicalPlanNumber: this._fcf.create(
        'COOPERATIVE_HOUSING.ECONOMICAL_PLAN_NUMBER',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.economicalPlanNumber : null
      ),
    });
    group.addControl(
      formControlName.apartmentNumber,
      this._fcf.create('DWELLING_FORM.APARTMENT_NUMBER_LABEL', this.dataContext.apartmentNumber, Validators.maxLength(10))
    );
    group.addControl(formControlName.building, this._fcf.createMandatory('DWELLING_FORM.BUILDING_LABEL', this.dataContext.buildingId));
    group.addControl(
      formControlName.buildingFloors,
      this._fcf.createWithOptions('DWELLING_FORM.BUILDING_FLOORS_LABEL', null, { isReadonly: true, noSubmit: true })
    );
    group.addControl(formControlName.elevator, this._fcf.create('DWELLING_FORM.ELEVATOR_LABEL', this.dataContext.elevator));
    group.addControl(formControlName.floor, this._fcf.create('DWELLING_FORM.FLOOR_LABEL', this.dataContext.floor));
    group.addControl(
      formControlName.indvidualmeterelectricity,
      this._fcf.create('DWELLING_FORM.INDIVIDUAL_METER_ELECTRICITY_LABEL', this.dataContext.indvidualMeterElectricity)
    );
    group.addControl(
      formControlName.indvidualmetercoldwater,
      this._fcf.create('DWELLING_FORM.INDIVIDUAL_METER_WATER_LABEL', this.dataContext.indvidualMeterColdWater)
    );
    group.addControl(
      formControlName.indvidualmeterwater,
      this._fcf.create('DWELLING_FORM.INDIVIDUAL_METER_WATER_LABEL', this.dataContext.indvidualMeterWater)
    );
    group.addControl(
      formControlName.indvidualmeterheating,
      this._fcf.create('DWELLING_FORM.INDIVIDUAL_METER_HEATING_LABEL', this.dataContext.indvidualMeterHeating)
    );
    group.addControl(
      formControlName.numberOfRooms,
      this._fcf.create('DWELLING_FORM.NUMBER_OF_ROOMS_LABEL', this.dataContext.numberOfRooms)
    );
    group.addControl(formControlName.entrance, this._fcf.createMandatory('ENTRANCE.ENTRANCE', this.dataContext.entranceId));
    group.addControl(formControlName.furnished, this._fcf.create('DWELLING.FURNISHED', this.dataContext.furnished));
    group.addControl(
      formControlName.atticStorageNumber,
      this._fcf.create('DWELLING_LEASE_AGREEMENT_FORM.ATTIC_STORAGE_NUMBER_LABEL', this.dataContext.atticStorageNumber)
    );
    group.addControl(
      formControlName.basementStorageNumber,
      this._fcf.create('DWELLING_LEASE_AGREEMENT_FORM.BASEMENT_STORAGE_NUMBER_LABEL', this.dataContext.basementStorageNumber)
    );
    group.addControl(
      formControlName.otherStorageNumber,
      this._fcf.create('DWELLING_LEASE_AGREEMENT_FORM.OTHER_STORAGE_NUMBER_LABEL', this.dataContext.otherStorageNumber)
    );
    group.addControl(
      formControlName.parkingIncludedInRent,
      this._fcf.create('DWELLING_LEASE_AGREEMENT_FORM.PARKING_INCLUDED_IN_RENT_LABEL', this.dataContext.parkingIncludedInRent)
    );
    group.addControl('surface', this._fcf.create('RENTAL_OBJECT_FORM.SURFACE_BOA_LONG', this.dataContext.surface));
    group.addControl('formOfLease', this._fcf.create('RENTAL_OBJECT.FORM_OF_LEASE', this.dataContext.formOfLease));
    group.addControl('rooms', this.fb.array([]));
    group.addControl('keys', this.fb.array([]));
    group.addControl(
      'imdMeterElectricity',
      this.createMeterForm(
        group,
        this.dataContext.imdMeterElectricity,
        'CONSUMPTION.METER_NAME.ELECTRICITY',
        'DWELLING_FORM.INDIVIDUAL_METER_ELECTRICITY_LABEL',
        this.dataContext.indvidualMeterElectricity,
        formControlName.indvidualmeterelectricity
      )
    );
    group.addControl(
      'imdMeterColdWater',
      this.createMeterForm(
        group,
        this.dataContext.imdMeterColdWater,
        'CONSUMPTION.METER_NAME.COLD_WATER',
        'DWELLING_FORM.INDIVIDUAL_METER_COLD_WATER_LABEL',
        this.dataContext.indvidualMeterColdWater,
        formControlName.indvidualmetercoldwater
      )
    );
    group.addControl(
      'imdMeterHotWater',
      this.createMeterForm(
        group,
        this.dataContext.imdMeterHotWater,
        'CONSUMPTION.METER_NAME.HOT_WATER',
        'DWELLING_FORM.INDIVIDUAL_METER_WATER_LABEL',
        this.dataContext.indvidualMeterWater,
        formControlName.indvidualmeterwater
      )
    );
    group.addControl(
      'imdMeterHeating',
      this.createMeterForm(
        group,
        this.dataContext.imdMeterHeating,
        'CONSUMPTION.METER_NAME.HEATING',
        'DWELLING_FORM.INDIVIDUAL_METER_HEATING_LABEL',
        this.dataContext.indvidualMeterHeating,
        formControlName.indvidualmeterheating
      )
    );
    group.addControl('brfSpecifics', this.brfForm);
    this.AddSubscription(group.controls['rooms'].valueChanges.subscribe(val => this.form.markAsDirty()));
    this.AddSubscription(group.controls['rooms'].statusChanges.subscribe(val => this.form.markAsDirty()));
    group.addControl('rentalObjectCustomProperties', this._createPropertiesForm(this.dataContext.rentalObjectCustomProperties));

    return group;
  }

  get heatingPaymentOptions() {
    return this.showBrf ? this.heatingPaymentsBrf : this.heatingPayments;
  }

  get coldWaterPaymentOptions() {
    return this.showBrf ? this.coldWaterPaymentsBrf : this.coldWaterPayments;
  }

  get hotWaterPaymentOptions() {
    return this.showBrf ? this.hotWaterPaymentsBrf : this.hotWaterPayments;
  }

  get householdElectricityPaymentOptions() {
    return this.showBrf ? this.householdElectricityPaymentsBrf : this.householdElectricityPayments;
  }

  private createMeterForm(
    form: FormGroup,
    meter: RentalObjectConsumptionMeter,
    nameLabel: string,
    enableLabel: string,
    enabled: boolean,
    enableField: string
  ) {
    let group = this.fb.group({
      id: this._fcf.createHidden(meter ? meter.id : null),
      name: this._fcf.create(nameLabel, meter ? meter.name : null),
      imdMeterModelId: this._fcf.create('CONSUMPTION.METER_MODEL.NAME', meter ? meter.imdMeterModelId : null),
      imdMeterId: this._fcf.create('CONSUMPTION.METER.METER', meter ? meter.imdMeterId : null),
      enabled: this._fcf.create(enableLabel, enabled),
    });

    this.AddSubscription(
      group.controls['enabled'].valueChanges.subscribe(val => {
        form.controls[enableField].setValue(val);
      })
    );

    this.AddSubscription(
      group.controls['imdMeterId'].valueChanges.subscribe(val => {
        let meter = this.propertyMeters.find(m => m.id === val);
        if (meter) {
          group.controls['imdMeterModelId'].setValue(meter.imdMeterModelId);
        }
      })
    );

    return group;
  }

  get propertyId(): string {
    let control = this.form.controls['property'];
    return control ? control.value : null;
  }

  propertyMeters: Meter[];
  allowedFormsOfLease: SelectionItem[] = [];
  changedAt: Date;
  componentAdded: boolean;
  rentalObjectDocumentCount = 0;

  linkDocument() {
    const cmd = new Command<Dwelling>(rentalObjectCommand.linkDocument);
    cmd.context = this.dataContext;
    this.commandDispatcher.sendCommand(cmd);
  }

  protected init(dwelling: Dwelling) {
    super.init(dwelling);
    this.changedAt = dwelling.changedAt;
    this.allowedFormsOfLease = dwelling.allowedFormsOfLease
      ? dwelling.allowedFormsOfLease.map(f => new SelectionItem(FormOfLeaseToString(f), f))
      : [];

    this.AddSubscription(
      this._buildingService.getList().subscribe(data => {
        this._buildings = data;
        if (!this.buildings) {
          let property = this.form.controls['property'].value;
          if (property) {
            this._filterBuildingsByProperty(property);
          }
          this._setBuildingFloors(this.form.controls[formControlName.building].value);
        }
      })
    );

    this.AddSubscription(
      this.form.controls['company'].valueChanges
        .pipe(switchMap(value => this._dataService.getValidFormsOfLease(value)))
        .subscribe(formsOfLease => {
          this.allowedFormsOfLease = formsOfLease.map(f => new SelectionItem(FormOfLeaseToString(f), f));
          if (!this.allowedFormsOfLease.find(f => f.value === this.form.controls['formOfLease'].value)) {
            this.form.controls['formOfLease'].setValue(this.allowedFormsOfLease[0].value);
          }
        })
    );

    if (dwelling.propertyId) {
      this.AddSubscription(this._meterService.getPropertyMeters(dwelling.propertyId).subscribe(meters => (this.propertyMeters = meters)));
    }

    this.AddSubscription(
      this._entranceService.getList().subscribe(data => {
        this._entrances = data;
        if (!this.entrances) {
          let building = this.form.controls['building'].value;
          if (building) {
            this._filterEntranceByBuilding(building);
          }
        }
      })
    );

    this.AddSubscription(
      this.form.controls[formControlName.building].valueChanges.subscribe(value => {
        this._setBuildingFloors(value);
        if (this._buildings) {
          let b = this._buildings.find(b => b.id === value);
          if (b) {
            this.setAddress(b.address);
          }
        }
      })
    );

    this.form.controls[formControlName.rentalObjectType].valueChanges.subscribe(value => {
      if (this.rentalObjectTypes) {
        let rentalObjectType = this.rentalObjectTypes.find(rType => rType.value === value);
        if (rentalObjectType) {
          if (rentalObjectType.numberOfRooms > 0)
            this.form.controls[formControlName.numberOfRooms].setValue(rentalObjectType.numberOfRooms);
        }
      }
    });

    this.AddSubscription(
      this.form.controls['property'].valueChanges
        .pipe(
          tap(value => {
            this._filterBuildingsByProperty(value);
          }),
          switchMap(value => this._meterService.getPropertyMeters(value))
        )
        .subscribe(meters => {
          this.propertyMeters = meters;
        })
    );

    this.AddSubscription(
      this.form.controls['building'].valueChanges.subscribe(value => {
        this._filterEntranceByBuilding(value);
      })
    );

    this.AddSubscription(
      this.form.controls['entrance'].valueChanges.subscribe(value => {
        if (value) {
          let entrance = this.entrances.find(e => e.value === value);
          if (entrance && entrance.name.length > 3) {
            let street = this.createStreet(entrance.name, this.form.controls[formControlName.apartmentNumber].value);
            if (street && street.length > 0) this.addressView.street.setValue(street);
          }
        }
      })
    );

    this.AddSubscription(
      this.form.controls[formControlName.apartmentNumber].valueChanges.subscribe(value => {
        let street = this.createStreet(<string>this.addressView.street.value, value);

        if (street && street.length > 0) this.addressView.street.setValue(street);

        if (value)
          if (value.substring(0, 2) === '09') this.form.controls[formControlName.floor].setValue(-1);
          else this.form.controls[formControlName.floor].setValue(value.substring(1, 2));
        else this.form.controls[formControlName.floor].setValue(null);
      })
    );
  }

  protected override initializeSection(section: string) {
    if (section === 'advertisments' && !this.isNew) {
      this.AddSubscription(
        this._advertService.getRentalObjectAdvertisments(this.dataContext.id).subscribe(result => {
          this.advertisments = result;
        })
      );
    }
    super.initializeSection(section);
  }

  private createStreet(street: string, apartmentNumber: string): string {
    if (!street) return '';

    street = street.trim();
    if (street.length === 0) return '';

    let index = street.indexOf(' lgh');
    if (index > 0) street = street.substring(0, index);

    if (apartmentNumber) street += ` lgh ${apartmentNumber}`;

    return street;
  }

  private _getAgreementPath(category: LeaseAgreementCategory) {
    let segment = '';
    switch (category) {
      case LeaseAgreementCategory.Block:
      case LeaseAgreementCategory.Dwelling:
      case LeaseAgreementCategory.Parking:
      case LeaseAgreementCategory.Premises:
        segment = LeaseAgreementCategory[category].toLowerCase();
        break;

      case LeaseAgreementCategory.Other:
        segment = 'other-rental-object';
        break;

      case LeaseAgreementCategory.CooperativeHousing:
        segment = 'co-housing';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  onRowClicked(e: { context: Contract; event: any }): void {
    this.navigation.navigate([this._getAgreementPath(e.context.category), 'contracts', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onInspectionRowClicked(e: { context: DwellingInspection; event: any }): void {
    this.navigation.navigate(['dwelling-inspection', 'inspection', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onApplicationRowClicked(e: { context: RentApplication; event: any }): void {
    this.navigation.navigate(['rent-application', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onAdvertismentRowClicked(e: { context: LettingAdvertisment; event: any }): void {
    this.navigation.navigateChild([this.dataContext.id, 'advertisments', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  formSubmitted() {
    super.formSubmitted();
    if (this.componentAdded) this.navigation.reloadCurrentRoute();
  }

  protected prepareSave(): Dwelling {
    let model = super.prepareSave();
    model.changedAt = this.changedAt;
    if (!model.brfSpecifics.assignmentFee || !model.brfSpecifics.assignmentFee.amount)
      model.brfSpecifics.assignmentFee = new Money(model.currencyCode, 0);
    if (!model.brfSpecifics.baseAnnualFee || !model.brfSpecifics.baseAnnualFee.amount)
      model.brfSpecifics.baseAnnualFee = new Money(model.currencyCode, 0);
    if (!model.brfSpecifics.contributionAmount || !model.brfSpecifics.contributionAmount.amount)
      model.brfSpecifics.contributionAmount = new Money(model.currencyCode, 0);
    model.buildingId = this.form.controls[formControlName.building].value;
    model.entranceId = this.form.controls[formControlName.entrance].value;
    model.rooms = this.roomsLoaded
      ? (this.form.controls['rooms'] as FormArray).controls.map(v => {
          let room = new Room(v.value);
          room.components = ((v as FormGroup).controls['components'] as FormArray).controls
            .map(g => g as FormGroup)
            .map(g => {
              let component = new ObjectComponent(g.value);
              component.customPropertyValues = (g.controls['customPropertyValues'] as FormArray).controls
                .map(c => c as FormGroup)
                .map(c => {
                  let value = new ObjectComponentPropertyValue(c.value);
                  value.dataType = c.value.dataType;
                  return value;
                });
              // At this component we expect to get 1 or 0 actions
              component.plannedMaintenanceActions = (g.controls['plannedMaintenanceActions'] as FormArray).controls
                .map(c => c as FormGroup)
                .map(a => new PlannedMaintenanceAction(a.value))
                .filter(
                  a =>
                    a.plannedMaintenanceCaption &&
                    a.plannedMaintenanceCaption.trim().length > 0 &&
                    a.plannedMaintenanceInterval &&
                    a.plannedMaintenanceInterval > 0
                )
                .slice(0, 1);
              return component;
            });
          return room;
        })
      : null;
    model.keys = (this.form.controls['keys'] as FormArray).controls.map(v => new RentalObjectKey(v.value));
    return model;
  }

  roomsLoaded: boolean;

  addedComponent() {
    this.componentAdded = true;
  }

  setRoomsVisibility(show: boolean) {
    if (this.roomsLoaded) {
      return;
    }
    this.AddSubscription(
      this._roomService.getByRentalObject(this.dataContext.id).subscribe(v => {
        this.dataContext.rooms = v;
        this.roomsLoaded = true;
      })
    );
  }

  consumptionIsExpanded = false;
  consumptionExpanded(expanded: boolean): void {
    this.consumptionIsExpanded = true;
  }

  mediaTypeColdWater = MeterMediaType.ColdWater;
  mediaTypeElectricity = MeterMediaType.Electricity;
  mediaTypeHotWater = MeterMediaType.WarmWater;
  mediaTypeHeating = MeterMediaType.Heating;

  //
  // Everything below related to custom property
  //
  customProperties: {
    form: FormGroup;
    selection: SelectionItem[];
    showInt: boolean;
    showText: boolean;
    showDate: boolean;
    showBoolean: boolean;
    showSelection: boolean;
    showNumeric: boolean;
  }[];

  private _createPropertiesForm(rentalObjectCustomProperties: LinkedCustomProperty[]): FormArray {
    const formPropertiesArray = new UntypedFormArray([]);
    this.customProperties = [];
    for (let rentalProperty of rentalObjectCustomProperties) {
      let form = this.fb.group({
        id: this._fcf.createHidden(rentalProperty.id),
        customPropertyId: this._fcf.createHidden(rentalProperty.customPropertyId),
        numericValue: this._fcf.create(rentalProperty.caption, rentalProperty.numericValue),
        textValue: this._fcf.create(rentalProperty.caption, rentalProperty.textValue),
        dateValue: this._fcf.create(rentalProperty.caption, LocalDate.fromDate(rentalProperty.dateValue)),
        boolValue: this._fcf.create(rentalProperty.caption, rentalProperty.boolValue),
        dataType: this._fcf.create(rentalProperty.caption, rentalProperty.dataType),
      });
      formPropertiesArray.push(form);

      this.customProperties.push({
        form: form,
        selection: rentalProperty.selectableValues.map(x => new SelectionItem(x, x)),
        showInt: rentalProperty.dataType === CustomPropertyDataType.Numeric && rentalProperty.decimalCount === 0,
        showText: rentalProperty.dataType === CustomPropertyDataType.Text,
        showDate: rentalProperty.dataType === CustomPropertyDataType.Date,
        showBoolean: rentalProperty.dataType === CustomPropertyDataType.Boolean,
        showSelection: rentalProperty.dataType === CustomPropertyDataType.Selection,
        showNumeric: rentalProperty.dataType === CustomPropertyDataType.Numeric && rentalProperty.decimalCount !== 0,
      });
    }

    return formPropertiesArray;
  }
}
